import React, { useEffect, useState } from "react";
import { ShieldCheck, Loader2, AlertCircle, Search, Building2, Scale, BookOpen, Clock, FileCheck, UserCheck, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * RequestScreeningButton — hiring-stage action.
 *
 * ARCHITECTURAL NOTE (Feb 2026 verification-arch brief):
 * Creates a per-employer + per-candidate ``screening_request`` row that
 * never becomes visible to any other employer and never reuses another
 * employer's prior screening result.
 *
 * Auto-detects the calling surface — if a platform admin token is
 * present (``simonara_admin_token``) it uses the admin-scoped endpoint
 * ``POST /admin/screening-requests`` which requires an ``employer_id``.
 * Otherwise it uses the employer-scoped endpoint which takes ownership
 * from the session.
 */
const PACKAGES = [
    { id: "employment_basic", label: "Basic", detail: "SSN trace, county criminal (7yr), sex-offender registry." },
    { id: "employment_standard", label: "Standard", detail: "Basic + national + federal criminal (7yr) + MVR." },
    { id: "employment_professional", label: "Professional", detail: "Standard + employment + education verification." },
];

// Must match backend `CURRENT_BTB_ATTESTATION_VERSION` in routers/screening.py.
// Bump this in lock-step with the backend when attestation copy changes.
const BTB_ATTESTATION_VERSION = "2026-02-14";

export const RequestScreeningButton = ({
    candidateEmail, jobId, employerId, variant = "default", context = "employer",
}) => {
    const [open, setOpen] = useState(false);
    const [packageId, setPackageId] = useState("employment_standard");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [inlineError, setInlineError] = useState("");
    // Ban-the-Box / Fair Chance attestation — both required before submit.
    // Server enforces the conditional-offer attestation; the permissible-
    // purpose one is a client-side compliance gate matching FCRA § 604(b).
    const [attestConditionalOffer, setAttestConditionalOffer] = useState(false);
    const [attestPermissiblePurpose, setAttestPermissiblePurpose] = useState(false);
    // Pre-order compliance primer — opens a right-hand sheet with a
    // ~2-minute read on Fair Chance Act + adverse-action steps so a
    // hiring manager can't click "Screen" without first understanding
    // what they're authorizing.
    const [primerOpen, setPrimerOpen] = useState(false);
    // Employer picker (admin context only) — searches /admin/users?role=employer
    const [pickerQuery, setPickerQuery] = useState("");
    const [pickerResults, setPickerResults] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickedEmployer, setPickedEmployer] = useState(null); // {user_id, name, email, company_name}

    const isAdminContext = context === "admin";
    // Admin drawer flow lets ops pick the employer; standard employer flow
    // uses the session's own employer_id passed by the caller.
    const effectiveEmployerId = isAdminContext
        ? (pickedEmployer?.user_id || employerId)
        : employerId;

    // Debounced employer search — only fires in admin context when the
    // dialog is open and the user has typed at least 2 chars.
    useEffect(() => {
        if (!isAdminContext || !open) return;
        const q = pickerQuery.trim();
        if (q.length < 2) { setPickerResults([]); return; }
        const t = setTimeout(async () => {
            setPickerLoading(true);
            try {
                const { data } = await api.get("/admin/users", {
                    params: { role: "employer", q, page_size: 8 },
                });
                const rows = data?.results || data?.users || data || [];
                setPickerResults(Array.isArray(rows) ? rows : []);
            } catch {
                setPickerResults([]);
            } finally { setPickerLoading(false); }
        }, 220);
        return () => clearTimeout(t);
    }, [pickerQuery, isAdminContext, open]);

    const submit = async () => {
        if (!candidateEmail) {
            setInlineError("Cannot request a screening — candidate email is missing.");
            return;
        }
        if (isAdminContext && !effectiveEmployerId) {
            setInlineError("Pick which employer this screening is on behalf of before sending.");
            return;
        }
        if (!attestConditionalOffer || !attestPermissiblePurpose) {
            setInlineError("Both compliance attestations are required before a background check can be ordered.");
            return;
        }
        setBusy(true);
        setInlineError("");
        try {
            const endpoint = isAdminContext ? "/admin/screening-requests" : "/employer/screening-requests";
            const body = {
                candidate_email: candidateEmail,
                job_id: jobId || null,
                package_id: packageId,
                internal_note: note.trim() || undefined,
                ban_the_box_attestation: true,
                ban_the_box_attestation_version: BTB_ATTESTATION_VERSION,
            };
            if (isAdminContext) body.employer_id = effectiveEmployerId;
            const { data } = await api.post(endpoint, body);
            toast.success(
                data?.reused_existing
                    ? "A pending screening already exists for this candidate — nothing new sent."
                    : "Screening requested — the candidate will be asked to authorize before we process it."
            );
            setOpen(false);
            setNote("");
            setAttestConditionalOffer(false);
            setAttestPermissiblePurpose(false);
            setPickedEmployer(null);
            setPickerQuery("");
            return data;
        } catch (err) {
            const msg = formatApiError(err);
            setInlineError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const primerLink = (
        <button
            type="button"
            onClick={() => setPrimerOpen(true)}
            data-testid="request-screening-primer-open"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
            <BookOpen size={10} /> Read this first (2 min)
        </button>
    );

    const triggerBtn = variant === "compact" ? (
        <div className="inline-flex items-center gap-2 flex-wrap">
            <button
                onClick={() => { setInlineError(""); setOpen(true); }}
                data-testid="drawer-request-screening"
                className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold text-sky-700 dark:text-sky-400 hover:bg-sky-500/15"
            >
                <ShieldCheck size={10} /> Screen
            </button>
            {primerLink}
        </div>
    ) : (
        <div className="inline-flex items-center gap-3 flex-wrap">
            <Button
                variant="outline"
                onClick={() => { setInlineError(""); setOpen(true); }}
                data-testid="drawer-request-screening"
                className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
            >
                <ShieldCheck size={13} className="mr-2" /> Request Employment Screening
            </Button>
            {primerLink}
        </div>
    );

    return (
        <>
            {triggerBtn}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="request-screening-dialog">
                    <DialogHeader>
                        <DialogTitle className="inline-flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-600" />
                            Request Employment Background Screening
                        </DialogTitle>
                        <DialogDescription>
                            A separate hiring-stage screening tied only to your account and this candidate.
                            The candidate must review and authorize a disclosure before Simonara ops runs the check.
                            Not related to Simonara Verified™ Passport verifications and never reusable by another employer.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold text-foreground">Candidate email</label>
                            <div data-testid="request-screening-candidate-email" className="mt-1 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm font-mono">
                                {candidateEmail || "—"}
                            </div>
                        </div>
                        {isAdminContext && (
                            <div data-testid="request-screening-employer-picker">
                                <label className="text-xs font-semibold text-foreground">On behalf of which employer?</label>
                                {pickedEmployer ? (
                                    <div className="mt-1 flex items-center justify-between rounded-md border border-emerald-500 bg-emerald-500/[0.06] px-3 py-1.5 text-sm">
                                        <span className="inline-flex items-center gap-2 min-w-0">
                                            <Building2 size={13} className="text-emerald-600" />
                                            <span className="truncate">
                                                <strong className="text-foreground">{pickedEmployer.company_name || pickedEmployer.name || pickedEmployer.email}</strong>
                                                <span className="text-muted-foreground ml-2 text-xs">{pickedEmployer.email}</span>
                                            </span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => { setPickedEmployer(null); setPickerQuery(""); }}
                                            data-testid="request-screening-employer-clear"
                                            className="text-xs text-muted-foreground hover:text-foreground"
                                        >
                                            Change
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mt-1">
                                        <div className="relative">
                                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                data-testid="request-screening-employer-search"
                                                value={pickerQuery}
                                                onChange={(e) => setPickerQuery(e.target.value)}
                                                placeholder="Search by company name or email…"
                                                className="pl-7"
                                                autoFocus
                                            />
                                        </div>
                                        {(pickerLoading || pickerResults.length > 0) && (
                                            <div className="mt-1 rounded-md border border-border bg-card overflow-hidden">
                                                {pickerLoading ? (
                                                    <div className="px-3 py-2 text-xs text-muted-foreground inline-flex items-center gap-1">
                                                        <Loader2 size={11} className="animate-spin" /> Searching…
                                                    </div>
                                                ) : (
                                                    pickerResults.map((emp) => (
                                                        <button
                                                            key={emp.user_id || emp.id || emp.email}
                                                            type="button"
                                                            onClick={() => {
                                                                setPickedEmployer({
                                                                    user_id: emp.user_id || emp.id,
                                                                    name: emp.name,
                                                                    email: emp.email,
                                                                    company_name: emp.company_name,
                                                                });
                                                                setPickerQuery("");
                                                                setPickerResults([]);
                                                            }}
                                                            data-testid={`request-screening-employer-option-${emp.user_id || emp.id || emp.email}`}
                                                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 border-b border-border last:border-b-0"
                                                        >
                                                            <Building2 size={12} className="text-emerald-500 shrink-0" />
                                                            <span className="min-w-0 flex-1">
                                                                <div className="font-semibold text-foreground truncate">
                                                                    {emp.company_name || emp.name || emp.email}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground truncate">
                                                                    {emp.email}
                                                                </div>
                                                            </span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        {pickerQuery.trim().length >= 2 && !pickerLoading && pickerResults.length === 0 && (
                                            <div className="mt-1 text-xs text-muted-foreground">No employer matches for &ldquo;{pickerQuery}&rdquo;.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-semibold text-foreground">Package</label>
                            <div className="mt-1 space-y-1.5">
                                {PACKAGES.map((p) => (
                                    <label
                                        key={p.id}
                                        data-testid={`request-screening-package-${p.id}`}
                                        className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors ${
                                            packageId === p.id
                                                ? "border-emerald-500 bg-emerald-500/[0.06]"
                                                : "border-border hover:bg-muted"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="screening-package"
                                            value={p.id}
                                            checked={packageId === p.id}
                                            onChange={() => setPackageId(p.id)}
                                            className="mt-0.5"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-foreground">{p.label}</div>
                                            <div className="text-xs text-muted-foreground">{p.detail}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-foreground">Internal note (optional)</label>
                            <Textarea
                                data-testid="request-screening-note"
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Visible only to Simonara admin ops."
                            />
                        </div>
                        <div className="rounded-md bg-sky-500/[0.06] border border-sky-500/30 px-3 py-2.5 text-xs text-foreground" data-testid="btb-explainer">
                            <div className="inline-flex items-center gap-1.5 font-semibold text-sky-800 dark:text-sky-300">
                                <Scale size={12} /> Fair Chance &amp; FCRA compliance
                            </div>
                            <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc pl-4">
                                <li><strong className="text-foreground">Consent comes first.</strong> The candidate must sign a standalone disclosure &amp; written consent through Checkr before the check runs.</li>
                                <li><strong className="text-foreground">Conditional offer required.</strong> Under the CA Fair Chance Act (Gov. Code § 12952) and similar laws, a conditional employment offer must be in place first.</li>
                                <li><strong className="text-foreground">Adverse action workflow.</strong> If the report leads you to reconsider, Simonara runs the FCRA two-step adverse-action process (pre-notice, waiting period, final notice) with a 5-business-day dispute window.</li>
                            </ul>
                        </div>

                        <div className="rounded-md border border-border bg-card p-3 space-y-2" data-testid="btb-attestations">
                            <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={attestConditionalOffer}
                                    onChange={(e) => setAttestConditionalOffer(e.target.checked)}
                                    data-testid="btb-attest-conditional-offer"
                                    className="mt-0.5 shrink-0"
                                />
                                <span>
                                    I confirm a <strong>conditional offer of employment</strong> has already been extended to this candidate. I understand this is required by the CA Fair Chance Act and by Simonara platform policy for every US candidate.
                                </span>
                            </label>
                            <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={attestPermissiblePurpose}
                                    onChange={(e) => setAttestPermissiblePurpose(e.target.checked)}
                                    data-testid="btb-attest-permissible-purpose"
                                    className="mt-0.5 shrink-0"
                                />
                                <span>
                                    I certify a <strong>permissible purpose under the FCRA</strong> (15 U.S.C. § 1681b) for ordering this consumer report — namely, evaluating this candidate for employment — and I will comply with the FCRA adverse-action process if the report affects the offer.
                                </span>
                            </label>
                        </div>
                        {inlineError && (
                            <div
                                data-testid="request-screening-error"
                                className="rounded-md bg-rose-500/10 border border-rose-500/40 px-3 py-2 text-xs text-rose-800 dark:text-rose-300 inline-flex items-start gap-2"
                            >
                                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                                <span>{inlineError}</span>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="request-screening-cancel"
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={busy || !candidateEmail || (isAdminContext && !effectiveEmployerId) || !attestConditionalOffer || !attestPermissiblePurpose}
                            data-testid="request-screening-submit"
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                            Send request to candidate
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pre-order compliance primer — non-blocking side sheet.
                Loaded regardless of variant so a hiring manager can
                read the workflow before they open the request dialog. */}
            <Sheet open={primerOpen} onOpenChange={setPrimerOpen}>
                <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="screening-primer-sheet">
                    <SheetHeader>
                        <SheetTitle className="inline-flex items-center gap-2">
                            <BookOpen size={16} className="text-emerald-600" />
                            Ordering a background check
                        </SheetTitle>
                        <SheetDescription>
                            A 2-minute walkthrough of what happens when you click <em>Request Employment Screening</em>,
                            what the candidate sees, and what your obligations are.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-5 space-y-5 text-sm">
                        <section>
                            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
                                <UserCheck size={14} className="text-sky-600" /> 1 · Consent-first — always
                            </div>
                            <p className="mt-1 text-muted-foreground">
                                Nothing runs until the candidate signs a <strong className="text-foreground">standalone written disclosure</strong> collected by <a href="https://checkr.com" target="_blank" rel="noreferrer" className="underline hover:text-foreground">Checkr</a>. You place the order; Checkr emails the candidate; they authorize; only then does the check start. If they never sign, no report is produced.
                            </p>
                        </section>

                        <section>
                            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
                                <Scale size={14} className="text-emerald-600" /> 2 · Conditional offer required (Fair Chance Act)
                            </div>
                            <p className="mt-1 text-muted-foreground">
                                Under the <strong className="text-foreground">California Fair Chance Act (Gov. Code § 12952)</strong> and similar &ldquo;ban-the-box&rdquo; laws in other jurisdictions, you may only order a criminal-history check after extending a <strong className="text-foreground">conditional offer of employment</strong>. Simonara requires this attestation on every US order, regardless of the candidate&rsquo;s state, so we never accidentally run afoul of the strictest law.
                            </p>
                        </section>

                        <section>
                            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
                                <FileCheck size={14} className="text-sky-600" /> 3 · What you&rsquo;ll see back
                            </div>
                            <p className="mt-1 text-muted-foreground">
                                Employers and recruiters see one of two results: <strong className="text-foreground">Clear</strong> (proceed) or <strong className="text-foreground">Consider</strong> (a hit was found — Simonara Compliance reviews the details). You do <em>not</em> see raw report content, SSN, DOB, or Checkr adjudication data. Only Simonara&rsquo;s Compliance role can access that layer, and every access is audit-logged.
                            </p>
                        </section>

                        <section>
                            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
                                <Flag size={14} className="text-amber-600" /> 4 · If a Consider result affects your decision (FCRA two-step)
                            </div>
                            <ol className="mt-1 list-decimal pl-5 text-muted-foreground space-y-1">
                                <li><strong className="text-foreground">Pre-adverse-action notice</strong> — Simonara sends the candidate a copy of the report and a summary of their FCRA rights.</li>
                                <li><strong className="text-foreground">5-business-day dispute window</strong> — the candidate can dispute inaccuracies directly with Checkr; the clock is enforced by our platform.</li>
                                <li><strong className="text-foreground">Final adverse-action notice</strong> — after the window closes without a dispute (or after a completed re-investigation), Checkr auto-schedules the final notice. You never call this endpoint yourself.</li>
                            </ol>
                        </section>

                        <section>
                            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
                                <Clock size={14} className="text-slate-500" /> 5 · How long we keep the data
                            </div>
                            <p className="mt-1 text-muted-foreground">
                                Simonara auto-purges background-check records <strong className="text-foreground">2 years after completion</strong>. Adverse-action records are retained longer per FCRA rules. Candidates can request earlier deletion from their <em>Data Requests</em> page; Compliance reviews and approves.
                            </p>
                        </section>

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                            <a href="/trust-center#background-checks" target="_blank" rel="noreferrer"
                                data-testid="screening-primer-trust-link"
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15">
                                <ShieldCheck size={11} /> Trust Center
                            </a>
                            <a href="/verification-consent-policy#background" target="_blank" rel="noreferrer"
                                data-testid="screening-primer-vcp-link"
                                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">
                                Full consent policy
                            </a>
                            <button
                                onClick={() => { setPrimerOpen(false); setInlineError(""); setOpen(true); }}
                                data-testid="screening-primer-start-order"
                                className="ml-auto inline-flex items-center gap-1 rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-semibold hover:opacity-90"
                            >
                                <ShieldCheck size={11} /> Got it — start the order
                            </button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
};

export default RequestScreeningButton;
